import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import useNavigationWarning from '../hooks/useNavigationWarning';
import { logActivity, getSessionId } from '../utils/sessionManager';
import Modal from '../components/Modal';
import OnScreenKeyboard from '../components/OnScreenKeyboard';
import config from '../config';
import manSilhouette from '../assets/images/mansilhouette.png';
import womanSilhouette from '../assets/images/womansilhouette.png';
import './GeneratePage.css';

const GeneratePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { superhero, userPhoto, pose, email } = location.state || {};
  const hasGenerationData = Boolean(superhero && userPhoto && pose && email);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [chamberOpening, setChamberOpening] = useState(false);
  const [currentLoadingText, setCurrentLoadingText] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [sendingAdditional, setSendingAdditional] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [keyboardTargetIndex, setKeyboardTargetIndex] = useState(null);
  const emailInputRefs = useRef([]);
  const hasStartedGenerating = useRef(false);

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Add navigation warning
  const { NavigationWarningModal } = useNavigationWarning(true);

  // Log page visit
  useEffect(() => {
    logActivity('generate_page_visited', {
      superhero: superhero?.name,
      pose: pose?.name,
      email
    });
  }, [superhero, pose, email]);

  // Auto-start generation when all required data is present
  useEffect(() => {
    if (superhero && userPhoto && pose && email && !isGenerating && !isComplete && !hasStartedGenerating.current) {
      hasStartedGenerating.current = true;
      handleGenerate();
    }
  }, [superhero, userPhoto, pose, email, isGenerating, isComplete]);

  // Determine if the selected superhero is typically female
  const isFemaleHero = superhero?.name && 
    ['Batgirl', 'Black Widow', 'Captain Marvel', 'Scarlet Witch', 'Wonder Woman'].includes(superhero.name);

  const handleGenerate = useCallback(async () => {
    const loadingTexts = [
      "Initializing transformation chamber...",
      "Scanning DNA sequence...",
      "Activating power core...",
      "Injecting superhero serum...",
      "Enhancing abilities...",
      "Synchronizing costume matrix...",
      "Finalizing transformation...",
      "Charging energy reserves..."
    ];

    setIsGenerating(true);
    setChamberOpening(false);

    // Log generation start
    logActivity('generation_button_clicked', {
      superhero: superhero?.name,
      pose: pose?.name
    });

    // Cycle through loading texts
    let textIndex = 0;
    const textInterval = setInterval(() => {
      setCurrentLoadingText(loadingTexts[textIndex]);
      textIndex = (textIndex + 1) % loadingTexts.length;
    }, 2000);

    try {
      // Get session ID
      const sessionId = await getSessionId();

      const response = await fetch(`${config.API_BASE_URL}/api/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userPhoto: userPhoto,
          superhero: superhero,
          pose: pose,
          email: email,
          session_id: sessionId  // Include session ID
        })
      });

      const data = await response.json();

      if (data.success) {
        clearInterval(textInterval);
        setCurrentLoadingText("Transformation complete!");
        
        logActivity('generation_successful', {
          superhero: superhero?.name,
          pose: pose?.name,
          email_sent: data.email_sent
        });
        
        // Trigger chamber opening animation
        setChamberOpening(true);
        
        // Wait for chamber animation to complete before showing image
        setTimeout(() => {
          setGeneratedImage(data.image);
          setIsGenerating(false);
          setIsComplete(true);
        }, 2000); // 2 seconds for chamber opening + flash
      } else {
        throw new Error(data.message || 'Generation failed');
      }
    } catch (error) {
      console.error('Error:', error);
      clearInterval(textInterval);
      setIsGenerating(false);
      
      logActivity('generation_failed', {
        error: error.message,
        superhero: superhero?.name,
        pose: pose?.name
      });
    }
  }, [userPhoto, superhero, pose, email]);

  const handleStartOver = () => {
    logActivity('start_over_clicked');
    navigate('/', { replace: true });
  };

  const handleBack = () => {
    logActivity('navigation_back_from_generate');
    navigate('/select-pose', { state: { superhero, userPhoto, email } });
  };

  const handleSendToAdditionalEmails = useCallback(async () => {
    const emailsToSend = additionalEmails.filter(email => isValidEmail(email));
    if (emailsToSend.length === 0) return;

    setSendingAdditional(true);
    
    logActivity('send_to_friends_clicked', {
      friend_count: emailsToSend.length
    });

    try {
      // Get session ID
      const sessionId = await getSessionId();

      const response = await fetch(`${config.API_BASE_URL}/api/send-to-emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: generatedImage,
          emails: emailsToSend,
          session_id: sessionId  // Include session ID
        })
      });

      const data = await response.json();
      if (data.success) {
        setModalState({
          isOpen: true,
          title: 'SUCCESS!',
          message: `Image sent to ${emailsToSend.length} additional email(s)!`,
          onConfirm: () => {
            setModalState({ isOpen: false, title: '', message: '', onConfirm: null });
            setAdditionalEmails([]);
          }
        });

        logActivity('send_to_friends_successful', {
          friend_count: emailsToSend.length
        });
      } else {
        setModalState({
          isOpen: true,
          title: 'FAILED',
          message: 'Failed to send to additional emails. Please try again.',
          onConfirm: () => setModalState({ isOpen: false, title: '', message: '', onConfirm: null })
        });
        logActivity('send_to_friends_failed', {
          error: data.error
        });
      }
    } catch (error) {
      console.error('Error sending to additional emails:', error);
      setModalState({
        isOpen: true,
        title: 'ERROR',
        message: 'Error sending to additional emails. Please try again.',
        onConfirm: () => setModalState({ isOpen: false, title: '', message: '', onConfirm: null })
      });

      logActivity('send_to_friends_error', {
        error: error.message
      });
    } finally {
      setSendingAdditional(false);
    }
  }, [additionalEmails, generatedImage]);

  const handleEmailChange = (index, value) => {
    const newEmails = [...additionalEmails];
    newEmails[index] = value;
    setAdditionalEmails(newEmails);
  };

  const handleAddEmail = () => {
    if (additionalEmails.length < 3) {
      setAdditionalEmails([...additionalEmails, '']);
      logActivity('add_friend_email_clicked');
    }
  };

  const handleRemoveEmail = (index) => {
    const newEmails = additionalEmails.filter((_, i) => i !== index);
    setAdditionalEmails(newEmails);
    logActivity('remove_friend_email_clicked');
  };

  const handleHomeClick = () => {
    logActivity('navigation_home_from_generate');
    navigate('/', { replace: true });
  };

  const handleKeyboardInput = useCallback((key) => {
    if (keyboardTargetIndex !== null) {
      if (key === 'Backspace') {
        const newEmails = [...additionalEmails];
        newEmails[keyboardTargetIndex] = newEmails[keyboardTargetIndex].slice(0, -1);
        setAdditionalEmails(newEmails);
      } else if (key === 'OK') {
        setShowKeyboard(false);
        setKeyboardTargetIndex(null);
      } else {
        const newEmails = [...additionalEmails];
        newEmails[keyboardTargetIndex] = (newEmails[keyboardTargetIndex] || '') + key;
        setAdditionalEmails(newEmails);
      }
    }
  }, [keyboardTargetIndex, additionalEmails]);

  const handleShowKeyboard = (index) => {
    setKeyboardTargetIndex(index);
    setShowKeyboard(true);
  };

  const handleCloseKeyboard = () => {
    setShowKeyboard(false);
    setKeyboardTargetIndex(null);
  };

  // Handle physical keyboard input when on-screen keyboard is active
  useEffect(() => {
    if (!showKeyboard || keyboardTargetIndex === null) return;

    const handleKeyDown = (event) => {
      // Keep focus on the current input field
      const currentInput = emailInputRefs.current[keyboardTargetIndex];
      if (currentInput && document.activeElement !== currentInput) {
        currentInput.focus();
      }

      // Prevent default behavior for certain keys to avoid conflicts
      if (event.key === 'Enter') {
        event.preventDefault();
        handleKeyboardInput('OK');
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        handleKeyboardInput('Backspace');
      } else if (event.key === ' ') {
        event.preventDefault();
        handleKeyboardInput(' ');
      } else if (event.key.length === 1) {
        // Allow single character keys (letters, numbers, symbols)
        event.preventDefault();
        handleKeyboardInput(event.key);
      }
      // Ignore other keys like Shift, Ctrl, etc.
    };

    // Ensure input field gets focus when keyboard opens
    const currentInput = emailInputRefs.current[keyboardTargetIndex];
    if (currentInput) {
      currentInput.focus();
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showKeyboard, keyboardTargetIndex, handleKeyboardInput]);

  return (
    <div className="generate-page">
      <div className="superhero-background"></div>

      {/* Render the navigation warning modal */}
      {NavigationWarningModal}

      <motion.button
        className="back-button"
        onClick={handleBack}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        ← BACK
      </motion.button>

      <motion.button
        className="home-button"
        onClick={handleHomeClick}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        HOME
      </motion.button>

      <div className="content-container">
        <AnimatePresence mode="wait">
          {!isGenerating && !isComplete && !hasGenerationData && (
            <motion.div
              key="summary"
              className="summary-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <div className="selection-summary">
                <h2 className="summary-title">Your Selection</h2>
                <div className="summary-details">
                  <p><strong>Hero:</strong> {superhero?.name}</p>
                  <p><strong>Pose:</strong> {pose?.name}</p>
                  <p><strong>Photo:</strong> Captured ✓</p>
                  <p><strong>Email:</strong> {email}</p>
                </div>
              </div>

              <h1 className="page-title">READY TO GENERATE</h1>
              <p className="page-subtitle">Click below to unleash the superhero within you!</p>

              <motion.button
                className="generate-button"
                onClick={handleGenerate}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                GENERATE SUPERHERO
              </motion.button>
            </motion.div>
          )}

          {(isGenerating || (!isComplete && hasGenerationData)) && (
            <motion.div
              key="loading"
              className="transformation-loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Transformation Chamber Container */}
              <div className="chamber-container">
                {/* Main Transformation Chamber */}
                <motion.div
                  className="transformation-chamber"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ 
                    scale: chamberOpening ? [1, 1.2, 1] : 1,
                    opacity: chamberOpening ? [1, 0.5, 0] : 1
                  }}
                  transition={{
                    duration: chamberOpening ? 1.5 : 0.5,
                    ease: "easeInOut"
                  }}
                >
                  {/* Chamber Walls */}
                  <div className="chamber-walls">
                    {/* Left Door */}
                    <motion.div
                      className="chamber-door chamber-door-left"
                      animate={{
                        x: chamberOpening ? "-100%" : "0%"
                      }}
                      transition={{
                        duration: 0.8,
                        ease: "easeOut"
                      }}
                    />
                    
                    {/* Right Door */}
                    <motion.div
                      className="chamber-door chamber-door-right"
                      animate={{
                        x: chamberOpening ? "100%" : "0%"
                      }}
                      transition={{
                        duration: 0.8,
                        ease: "easeOut"
                      }}
                    />

                    {/* Energy Glow Inside Chamber */}
                    <div className="chamber-glow" />
                  </div>

                  {/* Person Silhouette Being Transformed */}
                  <motion.div
                    className="person-silhouette"
                    animate={{
                      scale: chamberOpening ? [1, 1.2, 1.5] : [1, 1.05, 1],
                      opacity: chamberOpening ? [1, 0.7, 0] : [1, 0.9, 1]
                    }}
                    transition={{
                      duration: chamberOpening ? 0.8 : 3,
                      repeat: chamberOpening ? 0 : Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <img
                      src={isFemaleHero ? womanSilhouette : manSilhouette}
                      alt="Person silhouette"
                      className="silhouette-image"
                    />
                  </motion.div>

                  {/* Energy Particles Swirling Around */}
                  <div className="energy-particles">
                    {[...Array(12)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="energy-particle"
                        style={{
                          left: `${50 + 40 * Math.cos((i * 2 * Math.PI) / 12)}%`,
                          top: `${50 + 40 * Math.sin((i * 2 * Math.PI) / 12)}%`
                        }}
                        animate={{
                          scale: [0, 1, 0],
                          opacity: [0, 1, 0],
                          rotate: [0, 360]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: i * 0.15,
                          ease: "easeInOut"
                        }}
                      />
                    ))}
                  </div>

                  {/* Flash Effect When Opening */}
                  {chamberOpening && (
                    <motion.div
                      className="chamber-flash"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0, 1, 0] }}
                      transition={{ duration: 1, times: [0, 0.5, 1] }}
                    />
                  )}

                  {/* Chamber Base/Platform */}
                  <div className="chamber-base" />
                </motion.div>

                {/* Loading Text Below Chamber */}
                <motion.div
                  className="loading-text-display"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={currentLoadingText}
                      className="loading-text"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                    >
                      {currentLoadingText}
                    </motion.p>
                  </AnimatePresence>

                  {/* Progress Indicator */}
                  <div className="progress-bar-container">
                    <motion.div
                      className="progress-bar-fill"
                      animate={{ width: ["0%", "100%"] }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    />
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}

          {isComplete && (
            <motion.div
              key="complete"
              className="complete-section"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <motion.h1
                className="success-title"
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                YOUR DESTINY REVEALED! ⚡
              </motion.h1>

              <motion.div
                className="image-container"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {generatedImage && !imageError ? (
                  <img
                    src={generatedImage}
                    alt="Generated Superhero"
                    className="generated-image"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="image-error">
                    <p>Image failed to load. Please try generating again.</p>
                  </div>
                )}
              </motion.div>

              <motion.div
                className="ai-disclaimer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                <p className="ai-disclaimer-text">
                  <strong>Note:</strong> This image was generated using artificial intelligence.
                  While we strive for accuracy, AI-generated content may not always be perfect.
                  If you're not satisfied with the result, please try regenerating your superhero image.
                </p>
              </motion.div>

              <motion.p
                className="email-sent-message"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                ✉️ Image sent to: <strong>{email}</strong>
              </motion.p>

              <motion.div
                className="additional-emails-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <h3 className="additional-emails-title">SEND TO ADDITIONAL EMAILS</h3>
                <p className="additional-emails-subtitle">Share your superhero with up to 3 more friends!</p>
                <div className="email-inputs-container">
                  {additionalEmails.map((email, index) => (
                    <motion.div
                      key={index}
                      className="email-input-wrapper"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ delay: 0.9 + index * 0.1 }}
                    >
                      <div className="additional-email-input-container">
                        <input
                          ref={(el) => (emailInputRefs.current[index] = el)}
                          type="email"
                          placeholder={`Friend ${index + 1} email`}
                          value={email}
                          onChange={(e) => handleEmailChange(index, e.target.value)}
                          onFocus={() => {
                            if (config.enableOnScreenKeyboard) {
                              setKeyboardTargetIndex(index);
                              setShowKeyboard(true);
                            }
                          }}
                          onClick={() => {
                            if (config.enableOnScreenKeyboard) {
                              setKeyboardTargetIndex(index);
                              setShowKeyboard(true);
                            }
                          }}
                          onBlur={(e) => {
                            // Prevent blur when clicking on keyboard buttons
                            if (e?.relatedTarget?.closest('.onscreen-keyboard')) {
                              e.preventDefault();
                              const currentInput = emailInputRefs.current[index];
                              if (currentInput) {
                                currentInput.focus();
                              }
                            }
                          }}
                          className="additional-email-input"
                        />
                        {config.enableOnScreenKeyboard && (
                          <motion.button
                            className="keyboard-button-small"
                            onClick={() => handleShowKeyboard(index)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            type="button"
                            title="Open on-screen keyboard"
                          >
                            ⌨️
                          </motion.button>
                        )}
                      </div>
                      <button
                        type="button"
                        className="remove-email-button"
                        onClick={() => handleRemoveEmail(index)}
                        title="Remove email"
                      >
                        ×
                      </button>
                      {email.trim() && !isValidEmail(email) && (
                        <div className="email-error-message">
                          Invalid email format
                        </div>
                      )}
                    </motion.div>
                  ))}
                  {additionalEmails.length < 3 && (
                    <motion.button
                      className="add-email-button"
                      onClick={handleAddEmail}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1.0 }}
                    >
                      + Add Friend Email
                    </motion.button>
                  )}
                </div>
                {additionalEmails.filter(email => isValidEmail(email)).length > 0 && (
                  <motion.button
                    className="send-additional-button"
                    onClick={handleSendToAdditionalEmails}
                    disabled={sendingAdditional}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2 }}
                  >
                    {sendingAdditional ? 'SENDING...' : 'SEND TO FRIENDS'}
                  </motion.button>
                )}
              </motion.div>

              <motion.button
                className="start-over-button"
                onClick={handleStartOver}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.3 }}
              >
                CREATE ANOTHER
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Modal
        isOpen={modalState.isOpen}
        title={modalState.title}
        message={modalState.message}
        onConfirm={modalState.onConfirm}
      />

      {showKeyboard && config.enableOnScreenKeyboard && (
        <OnScreenKeyboard
          onKeyPress={handleKeyboardInput}
          onClose={handleCloseKeyboard}
          currentText={additionalEmails[keyboardTargetIndex] || ''}
        />
      )}
    </div>
  );
};

export default GeneratePage;
